import classNames from 'classnames';
import { action, observable, makeObservable, computed } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { Nav, NavItem, NavLink, TabContent, TabPane, Alert, Button, Card, CardBody, CardHeader, Badge } from 'reactstrap';
import { X7PushableBlockSnapshot, X8BlockSnapshot, X8BlockType } from '../../../domain';
import { X9StudentComponent } from '../../content/x9/X9StudentComponent';
import { ExamStudentSceneStore, ExamMistake } from './ExamStudentSceneStore';
import { useTranslation } from 'react-i18next';

function blockHeaderClassName(blockType: X8BlockType) {
  switch (blockType) {
    case 'Visible':
      return 'card-header alert-primary';
    case 'Pushable':
      return 'card-header alert-success';
    case 'Hidden':
      return 'card-header alert-warning';
    default:
      return 'card-header';
  }
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

@observer
class ExamTimer extends React.Component<{ store: ExamStudentSceneStore }> {
  render() {
    const { store } = this.props;
    
    if (!store.hasTimeLimit || store.remainingSeconds === null) {
      return null;
    }
    
    const isLow = store.remainingSeconds < 300; // Less than 5 minutes
    const isCritical = store.remainingSeconds < 60; // Less than 1 minute
    
    // Контрастный цвет текста: на жёлтом фоне — тёмный, на зелёном/красном — белый
    const textColor = isLow && !isCritical ? '#1a1a1a' : '#fff';
    
    return (
      <div className="exam-timer" style={{
        position: 'fixed',
        top: '70px',
        right: '20px',
        backgroundColor: isCritical ? '#dc3545' : (isLow ? '#ffc107' : '#28a745'),
        color: textColor,
        padding: '10px 20px',
        borderRadius: '8px',
        fontSize: '24px',
        fontWeight: 'bold',
        zIndex: 1000,
        boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
      }}>
        ⏱️ {formatTime(store.remainingSeconds)}
      </div>
    );
  }
}

// Functional component wrapper for translations
const ExamResultsContent: React.FC<{ store: ExamStudentSceneStore }> = observer(({ store }) => {
  const { t } = useTranslation();
  const result = store.examResult;
  
  if (!result) return null;
  
  const percentage = result.maxBal > 0 
    ? Math.round((result.totalBal / result.maxBal) * 100) 
    : 0;
  
  const hasNoQuestions = result.totalQuestions === 0 && result.maxBal === 0;
  const hasNoMistakes = result.mistakes.length === 0 && !hasNoQuestions;
  
  const getContentTypeName = (type: string): string => {
    return t(`exam.content-type.${type}`, type);
  };
  
  return (
    <Card className="mb-4">
      <CardHeader className="bg-primary text-white">
        <h4 className="mb-0">{t('exam.results.title')}</h4>
      </CardHeader>
      <CardBody>
        <div className="text-center mb-4">
          <h2>
            <Badge color={hasNoQuestions ? 'secondary' : (percentage >= 70 ? 'success' : percentage >= 50 ? 'warning' : 'danger')} 
                   style={{ fontSize: '2rem', padding: '15px 30px' }}>
              {t('exam.results.score', { score: result.totalBal, max: result.maxBal, percent: percentage })}
            </Badge>
          </h2>
          
          {result.totalQuestions > 0 && (
            <div className="mt-3" style={{ fontSize: '1.1rem', display: 'flex', flexWrap: 'wrap', gap: '0.75rem 1.5rem', justifyContent: 'center' }}>
              <span>{t('exam.results.total-questions')}: <strong>{result.totalQuestions}</strong></span>
              <span className="text-success">{t('exam.results.correct')}: <strong>{result.correctCount}</strong></span>
              <span className="text-danger">{t('exam.results.incorrect')}: <strong>{result.incorrectCount}</strong></span>
            </div>
          )}
        </div>
        
        {hasNoQuestions && (
          <Alert color="warning" className="text-center">
            {t('exam.results.no-answers')}
          </Alert>
        )}
        
        {result.mistakes.length > 0 && (
          <div>
            <h5 className="text-danger mb-3">{t('exam.results.mistakes')} ({result.mistakes.length}):</h5>
            {result.mistakes.map((mistake, index) => {
              const yourAnswer = mistake.studentAnswer || t('exam.results.no-answer');
              const isMatching = mistake.contentType === 'matching-question';
              const blockStyle = isMatching ? { whiteSpace: 'pre-line' as const } : undefined;
              const hasParts = mistake.studentAnswerParts && mistake.studentAnswerParts.length > 0;
              return (
                <Alert key={index} color="danger" className="mb-2">
                  <div style={{ marginBottom: '0.35rem' }}>
                    <strong style={{ color: '#6f42c1' }}>{t('exam.results.type')}:</strong>{' '}
                    <span style={{ color: '#495057' }}>{getContentTypeName(mistake.contentType)}</span>
                  </div>
                  <div style={{ marginBottom: '0.35rem' }}>
                    <strong style={{ color: '#0d6efd' }}>{t('exam.results.your-answer')}:</strong>{' '}
                    {hasParts ? (
                      <span style={blockStyle}>
                        {mistake.studentAnswerParts!.map((part, i) => (
                          <React.Fragment key={i}>
                            {i > 0 && (isMatching ? <br /> : ', ')}
                            <span style={{ color: part.isCorrect ? '#198754' : '#495057' }}>{part.text}</span>
                          </React.Fragment>
                        ))}
                      </span>
                    ) : (
                      <span style={{ color: '#495057', ...blockStyle }}>{yourAnswer}</span>
                    )}
                  </div>
                  <div>
                    <strong style={{ color: '#d63384' }}>{t('exam.results.correct-answer')}:</strong>{' '}
                    <span style={{ color: '#495057', ...blockStyle }}>{mistake.correctAnswer}</span>
                  </div>
                </Alert>
              );
            })}
          </div>
        )}
        
        {hasNoMistakes && (
          <Alert color="success" className="text-center">
            {t('exam.results.no-mistakes')}
          </Alert>
        )}
      </CardBody>
    </Card>
  );
});

@observer
class ExamResults extends React.Component<{ store: ExamStudentSceneStore }> {
  render() {
    return <ExamResultsContent store={this.props.store} />;
  }
}

// Functional component wrapper for translations
const FinishExamButtonContent: React.FC<{ store: ExamStudentSceneStore }> = observer(({ store }) => {
  const { t } = useTranslation();
  
  if (store.isExamFinished || store.isTimedOut) {
    return null;
  }
  
  return (
    <div className="text-center my-4">
      <Button 
        color="success" 
        size="lg"
        onClick={store.confirmFinishExam}
        style={{ padding: '15px 40px', fontSize: '18px' }}
      >
        ✅ {t('exam.finish.button')}
      </Button>
    </div>
  );
});

@observer
class FinishExamButton extends React.Component<{ store: ExamStudentSceneStore }> {
  render() {
    return <FinishExamButtonContent store={this.props.store} />;
  }
}

// Timeout warning with translation
const TimeoutWarningContent: React.FC<{ store: ExamStudentSceneStore }> = observer(({ store }) => {
  const { t } = useTranslation();
  
  if (!store.isTimedOut) {
    return null;
  }
  
  return (
    <Alert color="danger" className="text-center">
      ⏰ {t('exam.timer.time-up')}
    </Alert>
  );
});

@observer
class TimeoutWarning extends React.Component<{ store: ExamStudentSceneStore }> {
  render() {
    return <TimeoutWarningContent store={this.props.store} />;
  }
}

@observer
class RenderBlockSnapshot extends React.Component<{
  store: ExamStudentSceneStore,
  blockSnapshot: X8BlockSnapshot
}> {

  render() {
    const store = this.props.store;
    const block = this.props.blockSnapshot;

    return (
      <div className="card">
        <div className={blockHeaderClassName(block.BlockType)}>
          <div className="row">
            <div className="col"> Block</div>
          </div>
        </div>
        
        <div className="card-body">
          {block.ContentSnapshots?.length === 0 && 
            <div className="row justify-content-center">
              <div className="col-auto">
                <div className="alert alert-info">empty block</div>
              </div>
            </div>
          }

          {block.ContentSnapshots?.map((content) =>
            <X9StudentComponent key={content.Id}
              x9={content}
              store={store.hubStore.examKeyValues}
              lessonType="Examination" />
          )}
        </div>
      </div>
    );
  }
}



@observer
class RenderBlockSnapshots extends React.Component<{
  store: ExamStudentSceneStore,
  blocks: readonly X8BlockSnapshot[]
}> {
  render() {
    const store = this.props.store;
    
    const blocks = this.props.blocks ?? [];

    if (blocks === null || blocks.length === 0)
      return (
        <div className="row justify-content-center">
          <div className="col-auto">
            <div className="alert alert-info">empty pushable block</div>
          </div>
        </div>);

    return blocks.filter(x => x.BlockType !== 'Hidden').map((block) =>
      <RenderBlockSnapshot key={block.Id}
        store={store}
        blockSnapshot={block} />
    );
  }
}

@observer
class RenderPushableBlockSnapshot extends React.Component<{
  store: ExamStudentSceneStore,
  pushableBlockSnapshot: X7PushableBlockSnapshot
}> {

  render() {
    const pb = this.props.pushableBlockSnapshot;
    const store = this.props.store;
    return (
      <div className="card">
        <div className="card-header">
          <div>Pushable Block: {pb.Name}</div>
        </div>

        <div className="card-body">
          <RenderBlockSnapshots store={store}
            blocks={pb.BlockSnapshots!} />
        </div>
      </div>);
  }
}

@observer
class RenderPushableBlockSnapshots extends React.Component<{
  store: ExamStudentSceneStore,
  pushableBlockSnapshots: readonly X7PushableBlockSnapshot[]
}> {
  render() {
    const store = this.props.store;
    const snapshots = this.props.pushableBlockSnapshots;
    if (snapshots === null || snapshots.length === 0)
      return (
        <div className="row justify-content-center">
          <div className="col-auto">
            <div className="alert alert-info">empty page</div>
          </div>
        </div>);

    return snapshots.map((pb) =>
      <RenderPushableBlockSnapshot key={pb.Id}
        store={store}
        pushableBlockSnapshot={pb} />);
  }
}

@observer
export class StudentExamTabs extends React.Component<{ store: ExamStudentSceneStore }> {
  @observable
  activeTab: number = 0;

  @action
  setActiveTab = (tabIndex: number) => {
    this.activeTab = tabIndex;
    this.props.store.hubStore.examKeyValues.setActiveTab(tabIndex);
  };

  constructor(props: { store: ExamStudentSceneStore }) {
    super(props);
    makeObservable(this);
  }

  componentDidMount() {
    this.activeTab = this.props.store.hubStore.examKeyValues.activeTabIndex;;
  }

  render() {
    const store = this.props.store;
    const snapshot = store.snapshot!;
 
    if (snapshot === null)
      return null;

    const x6Snapshots = snapshot.PageSnapshots ?? [];

    if (x6Snapshots.length === 0)
      return (
        <div className="row">
          <div className="col">
            <div className="alert alert-info">Lesson is empty</div>
          </div>
        </div>);

    let activeTab = this.activeTab;
    const clampedTab = activeTab >= x6Snapshots.length ? 0 : activeTab;
    if (clampedTab !== activeTab) {
      this.setActiveTab(clampedTab);
      activeTab = clampedTab;
    }

    // Show results if exam is finished
    if (store.isExamFinished) {
      return (
        <div className="row">
          <div className="col">
            <ExamResults store={store} />
          </div>
        </div>
      );
    }

    return (
      <div className="row">
        <div className="col">
          {/* Timer */}
          <ExamTimer store={store} />
          
          {/* Timeout warning */}
          <TimeoutWarning store={store} />
          
          <Nav tabs>
            {x6Snapshots.map(
              (page, index) =>
                <NavItem key={page.Id}>
                  <NavLink className={classNames({ active: activeTab === index })}
                    onClick={() => this.setActiveTab(index)}>
                    {page.Name}
                  </NavLink>
                </NavItem>
            )
            }
          </Nav>
          <TabContent activeTab={activeTab}>
            {x6Snapshots.map(
              (page, index) =>
                <TabPane tabId={index} key={page.Id}>
                  <RenderPushableBlockSnapshots
                    store={store}
                    pushableBlockSnapshots={page.PushableBlockSnapshots!} />
                </TabPane>
            )}
          </TabContent>
          
          {/* Finish button */}
          <FinishExamButton store={store} />
        </div>
      </div>
    );
  }
}
